import { dayjs } from '@/utilities';

import type { FilingCategory, FilingSourceEntity, SECFiling } from './types';

interface FilingFilterRule {
    field: string;
    operator: 'contains' | 'equals' | 'greaterThan' | 'lessThan' | 'notEquals';
    value: boolean | number | string | Array<boolean | number | string>;
    valueJoin?: 'and' | 'or';
}

interface FilingQuoteHtmlOptions {
    quote: string;
    selectedFiling?: SECFiling;
    sourceEntity?: FilingSourceEntity;
    sourceUrl: string;
    symbol?: string;
}

interface FilingSourceOpenState {
    dismissedSourceKey?: string;
    matchedFilingUrl?: string;
    selectedFilingUrl?: string;
    sourceKey?: string;
}

export interface PopulatedFilingSection {
    category: FilingCategory;
    filings: SECFiling[];
}

export interface FilingSourceLocator {
    acceptedDate?: string | null;
    filingCik?: string | null;
    filingDate?: string | null;
    filingSymbol?: string | null;
    filingType?: string | null;
    filingUrl?: string | null;
    sourceAsReportedLabel?: string | null;
    sourceLineItem?: string | null;
    sourceLabel?: string | null;
    sourcePeriod?: string | null;
    sourceStatement?: string | null;
    sourceValue?: string | null;
}

export const FILING_DOCUMENT_BACKGROUND = '#ffffff';

export const applyLightFilingDocumentSurface = (filingDocument: Document) => {
    const surfaces = [filingDocument.documentElement, filingDocument.body].filter((surface): surface is HTMLElement =>
        Boolean(surface),
    );

    surfaces.forEach((surface) => {
        surface.style.setProperty('background-color', FILING_DOCUMENT_BACKGROUND, 'important');
        surface.style.setProperty('color-scheme', 'only light', 'important');
    });
};

export const shouldOpenFilingFromSource = ({
    dismissedSourceKey,
    matchedFilingUrl,
    selectedFilingUrl,
    sourceKey,
}: FilingSourceOpenState) =>
    Boolean(
        sourceKey && matchedFilingUrl && selectedFilingUrl !== matchedFilingUrl && dismissedSourceKey !== sourceKey,
    );

export const clearFilingSourceParams = (search: string) => {
    const params = new URLSearchParams(search);
    params.delete('acceptedDate');
    params.delete('filingDate');
    params.delete('filingCik');
    params.delete('filingSymbol');
    params.delete('filingType');
    params.delete('filingUrl');
    params.delete('filingText');
    params.delete('sourceStatement');
    params.delete('sourceAsReportedLabel');
    params.delete('sourceLineItem');
    params.delete('sourceLabel');
    params.delete('sourcePeriod');
    params.delete('sourceValue');
    const nextSearch = params.toString();

    return nextSearch ? `?${nextSearch}` : '';
};

export const getFilingType = (filing: SECFiling) => {
    return String(filing.type ?? filing.formType ?? '').toUpperCase();
};

export const getFilingUrl = (filing?: SECFiling) => {
    return filing?.finalLink || filing?.link || '';
};

const filingAccessionNumber = (filing: SECFiling) => {
    const directAccessionNumber = filing.accessionNumber?.replace(/\D/g, '');

    if (directAccessionNumber) {
        return directAccessionNumber;
    }

    for (const filingUrl of [filing.finalLink, filing.link]) {
        const accessionNumber = filingUrl?.match(/\/Archives\/edgar\/data\/\d+\/(\d{18})/i)?.[1]?.replace(/\D/g, '');

        if (accessionNumber) {
            return accessionNumber;
        }
    }

    return undefined;
};

export const getFilingIdentity = (filing: SECFiling) => {
    const accessionNumber = filingAccessionNumber(filing);

    if (accessionNumber) {
        return filing.kind === 'investor-material'
            ? `accession:${accessionNumber}:investor-material:${getFilingUrl(filing)}`
            : `accession:${accessionNumber}`;
    }

    return [
        filing.cik,
        filing.symbol,
        getFilingType(filing),
        comparableDate(filing.filingDate),
        filing.acceptedDate,
        filing.title,
    ]
        .filter(Boolean)
        .join('|');
};

const comparableUrl = (value?: string | null) => {
    if (!value) {
        return undefined;
    }

    try {
        return new URL(value).toString();
    } catch {
        return value;
    }
};

export const matchesFilingSourceUrl = (filing: SECFiling, sourceUrl?: string | null) => {
    const comparableSourceUrl = comparableUrl(sourceUrl);

    if (!comparableSourceUrl) {
        return false;
    }

    return [filing.finalLink, filing.link].some(
        (filingUrl) => filingUrl === sourceUrl || comparableUrl(filingUrl) === comparableSourceUrl,
    );
};

const comparableDate = (value?: string | null) => {
    const datePrefix = value?.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
    const date = datePrefix ?? value;

    return date && dayjs(date).isValid() ? dayjs(date).format('YYYY-MM-DD') : undefined;
};

const comparableFilingType = (value?: string | null) =>
    String(value ?? '')
        .trim()
        .toUpperCase();

export const getFilingSourceKey = ({
    acceptedDate,
    filingCik,
    filingDate,
    filingSymbol,
    filingType,
    filingUrl,
    sourceAsReportedLabel,
    sourceLineItem,
    sourceLabel,
    sourcePeriod,
    sourceStatement,
    sourceValue,
}: FilingSourceLocator) =>
    [
        filingUrl,
        filingCik,
        filingSymbol,
        comparableFilingType(filingType),
        comparableDate(filingDate),
        comparableDate(acceptedDate),
        sourceStatement,
        sourceAsReportedLabel,
        sourceLineItem,
        sourceLabel,
        sourcePeriod,
        sourceValue,
    ]
        .filter(Boolean)
        .join('|') || undefined;

export const getFilingSourceDateRange = ({ acceptedDate, filingDate }: FilingSourceLocator) => {
    const sourceDate = comparableDate(acceptedDate) ?? comparableDate(filingDate);

    if (!sourceDate) {
        return undefined;
    }

    return {
        from: dayjs(sourceDate).subtract(14, 'day').format('YYYY-MM-DD'),
        to: dayjs(sourceDate).add(14, 'day').format('YYYY-MM-DD'),
    };
};

export const matchesFilingSource = (filing: SECFiling, source: FilingSourceLocator) => {
    if (matchesFilingSourceUrl(filing, source.filingUrl)) {
        return true;
    }

    const sourceType = comparableFilingType(source.filingType);

    if (!sourceType || getFilingType(filing) !== sourceType) {
        return false;
    }

    const sourceFilingDate = comparableDate(source.filingDate);
    const sourceAcceptedDate = comparableDate(source.acceptedDate);

    if (!sourceFilingDate && !sourceAcceptedDate) {
        return true;
    }

    const filingDates = [comparableDate(filing.filingDate), comparableDate(filing.acceptedDate)].filter(Boolean);

    return Boolean(
        (sourceFilingDate && filingDates.includes(sourceFilingDate)) ||
        (sourceAcceptedDate && filingDates.includes(sourceAcceptedDate)),
    );
};

export const normalizeSelectionText = (value: string) => value.replace(/\s+/g, ' ').trim();

export const escapeHtml = (value: string) =>
    value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');

export const matchesFilingCategory = (filing: SECFiling, category: FilingCategory) => {
    if (category.key === 'all') {
        return filing.kind !== 'investor-material';
    }

    if (category.kinds?.length) {
        return Boolean(filing.kind && category.kinds.includes(filing.kind));
    }

    const type = getFilingType(filing);

    return category.forms?.some((form) => type === form || type.startsWith(`${form}/`) || type.startsWith(`${form} `));
};

export const getPopulatedFilingSections = (
    filings: SECFiling[],
    categories: FilingCategory[],
): PopulatedFilingSection[] =>
    categories.flatMap((category) => {
        const categoryFilings = filings.filter((filing) => matchesFilingCategory(filing, category));

        return categoryFilings.length || category.alwaysVisible ? [{ category, filings: categoryFilings }] : [];
    });

const filingFilterValue = (filing: SECFiling, field: string) => {
    switch (field) {
        case 'acceptedDate':
            return filing.acceptedDate;
        case 'filingDate':
            return filing.filingDate;
        case 'filingType':
            return getFilingType(filing);
        case 'title':
            return filing.title;
        default:
            return undefined;
    }
};

const normalizedRuleValues = (value: FilingFilterRule['value']) =>
    (Array.isArray(value) ? value : [value]).filter((item) => item !== '' && item !== null && item !== undefined);

const matchesFilingFilterValue = (
    actualValue: string | undefined,
    expectedValue: boolean | number | string,
    rule: FilingFilterRule,
) => {
    if (!actualValue) {
        return rule.operator === 'notEquals';
    }

    const isDateField = rule.field === 'acceptedDate' || rule.field === 'filingDate';
    const normalizedActual = isDateField ? comparableDate(actualValue) : actualValue.trim().toLowerCase();
    const normalizedExpected = isDateField
        ? comparableDate(String(expectedValue))
        : String(expectedValue).trim().toLowerCase();

    if (!normalizedActual || !normalizedExpected) {
        return false;
    }

    switch (rule.operator) {
        case 'contains':
            return normalizedActual.includes(normalizedExpected);
        case 'greaterThan':
            return normalizedActual > normalizedExpected;
        case 'lessThan':
            return normalizedActual < normalizedExpected;
        case 'notEquals':
            return normalizedActual !== normalizedExpected;
        case 'equals':
        default:
            return normalizedActual === normalizedExpected;
    }
};

export const filterFilings = (filings: SECFiling[], query: string, rules: FilingFilterRule[]) => {
    const normalizedQuery = query.trim().toLowerCase();

    return filings.filter((filing) => {
        const matchesQuery =
            !normalizedQuery ||
            [filing.title, filing.symbol, getFilingType(filing), filing.filingDate, filing.acceptedDate].some((value) =>
                value?.toLowerCase().includes(normalizedQuery),
            );

        return (
            matchesQuery &&
            rules.every((rule) => {
                const values = normalizedRuleValues(rule.value);
                const actualValue = filingFilterValue(filing, rule.field);
                const matches = values.map((value) => matchesFilingFilterValue(actualValue, value, rule));
                const join = rule.valueJoin ?? (rule.operator === 'notEquals' ? 'and' : 'or');

                return join === 'and' ? matches.every(Boolean) : matches.some(Boolean);
            })
        );
    });
};

const getEntityInitials = (value: string) => {
    return value
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join('');
};

const getFilingYear = (filing?: SECFiling) => {
    const date = filing?.filingDate ?? filing?.acceptedDate;

    return date && dayjs(date).isValid() ? dayjs(date).format('YYYY') : undefined;
};

const getCitationLabel = (filing?: SECFiling) => {
    if (!filing) {
        return 'SEC Filing';
    }

    const filingType = getFilingType(filing) || 'Filing';
    const filingYear = getFilingYear(filing);

    return filingYear ? `${filingType} (${filingYear})` : filingType;
};

const getSafeHttpUrl = (value?: string) => {
    if (!value) {
        return undefined;
    }

    try {
        const url = new URL(value);

        return ['http:', 'https:'].includes(url.protocol) ? url.toString() : undefined;
    } catch {
        return undefined;
    }
};

const getSafeWebsite = (value?: string) => {
    if (!value) {
        return undefined;
    }

    try {
        const normalizedValue = /^[a-z][a-z\d+.-]*:\/\//i.test(value) ? value : `https://${value}`;
        const url = new URL(normalizedValue);

        return ['http:', 'https:'].includes(url.protocol) ? url.toString() : undefined;
    } catch {
        return undefined;
    }
};

export const getFilingQuoteHtml = ({
    quote,
    selectedFiling,
    sourceEntity,
    sourceUrl,
    symbol,
}: FilingQuoteHtmlOptions) => {
    const companyName = sourceEntity?.name?.trim() || selectedFiling?.symbol || symbol || 'SEC Filing';
    const citationLabel = getCitationLabel(selectedFiling);
    const logoUrl = getSafeHttpUrl(sourceEntity?.logoUrl?.trim());
    const website = getSafeWebsite(sourceEntity?.website?.trim());
    const avatarInitials = escapeHtml(getEntityInitials(companyName) || companyName[0] || 'F');
    const logoAttribute = logoUrl ? ` data-logo-url="${escapeHtml(logoUrl)}"` : '';
    const websiteAttribute = website ? ` data-website="${escapeHtml(website)}"` : '';
    const escapedCompanyName = escapeHtml(companyName);
    const escapedCitationLabel = escapeHtml(citationLabel);

    return [
        '<blockquote class="blockquote-light filing-source-quote" contenteditable="false">',
        `<p class="filing-source-quote-text" contenteditable="false" data-filing-quote-text="true">${escapeHtml(normalizeSelectionText(quote))}</p>`,
        '<p class="filing-source-citation">',
        `<a class="filing-source-button" data-filing-source="true" data-avatar="${avatarInitials}" data-company="${escapedCompanyName}" data-filing="${escapedCitationLabel}" href="${escapeHtml(sourceUrl)}"${logoAttribute}${websiteAttribute}>`,
        `<span class="filing-source-company">${escapedCompanyName}</span>`,
        '<span aria-hidden="true" class="filing-source-chevron"></span>',
        `<span class="filing-source-filing">${escapedCitationLabel}</span>`,
        '</a>',
        '</p>',
        '</blockquote>',
        '<p></p>',
    ].join('');
};
